import moment from 'moment'
import React, { useCallback, useContext, useRef } from 'react'

import DatesDropdown from './DatesDropdown'
import DatesModal from './DatesModal'
import {
  isCustomSingleDateItem,
  itemIsInItinerary,
  customItemToUpdatePayload,
  itemTypesWithNoEndDate,
  canAddItemToItineraryWithExistingDate,
} from './utils'
import BaseBookmarkToItineraryButton from '../Bookmark/Common/BaseBookmarkToItineraryButton'
import DateRangeDropdown from '../TripModal/Common/DateRangeDropdown'
import MobileSelectDatesSelfClose from '../TripModal/Common/MobileSelectDatesSelfClose'

import ModalBase from 'components/Luxkit/Modal/ModalBase'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import ModalContext from 'contexts/ModalContext'
import useToggle from 'hooks/useToggle'
import noop from 'lib/function/noop'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import { AddToItineraryDatesRequiredModal } from 'tripPlanner/components/Itinerary/AddToItineraryDatesRequiredModal'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import { useTrip, useUpdateTripItem } from 'tripPlanner/hooks/api'
import { useAddItemToItinerary } from 'tripPlanner/hooks/api/tripItem'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import { CustomItem } from 'tripPlanner/types/tripItem'

export interface Props {
  item: CustomItem
  tripId?: string
  variant: 'chip' | 'button'
  onDatesRequiredMessage?: () => void
  onRequestAddTripDates: (
    onDatesAdded?: (startDate: moment.Moment, endDate: moment.Moment) => void,
  ) => void
}

function ItineraryDateButton({
  item: tripItem,
  tripId: tripIdProp,
  variant,
  onDatesRequiredMessage = noop,
  onRequestAddTripDates,
}: Props) {
  const contextTripId = useTripId()
  const tripId = tripIdProp ?? contextTripId
  const { data: trip } = useTrip({ tripId })
  const showModal = useContext(ModalContext)
  const isMobile = useIsMobileScreen()
  const { canEdit } = useTripPermissions(tripId)

  const triggerRef = useRef<HTMLButtonElement>(null)
  const {
    value: singleDateDropdownOpen,
    toggle: toggleSingleDateDropdown,
    off: closeSingleDateDropdown,
  } = useToggle()
  const {
    value: dateRangeDropdownOpen,
    toggle: toggleDateRangeDropdown,
    off: closeDateRangeDropdown,
  } = useToggle()

  const isInItinerary = itemIsInItinerary(tripItem, trip)

  const canAddWithOneClick = canAddItemToItineraryWithExistingDate(
    tripItem,
    trip,
  )

  const { mutate: updateItem, isLoading: isUpdating } = useUpdateTripItem()

  const { mutate: addToItinerary } = useAddItemToItinerary({
    onSuccess: () => {
      showSnackbar('Added to itinerary', 'success')
    },
  })

  const addItemToItineraryWithDates = useCallback(
    async (startDate: moment.Moment, endDate: moment.Moment) => {
      if (!trip || !tripItem) {
        return
      }
      const withEndDate = !itemTypesWithNoEndDate.includes(tripItem.type as any)
      const payload = {
        ...customItemToUpdatePayload(tripItem),
        isInItinerary: true,
        startDate: startDate.format(ISO_DATE_FORMAT),
        endDate: withEndDate ? endDate.format(ISO_DATE_FORMAT) : undefined,
      }
      updateItem(
        {
          tripId: trip.id,
          tripItemId: tripItem.id,
          tripItem: payload,
        },
        {
          onSuccess: () => {
            showSnackbar('Added to itinerary', 'success')
          },
        },
      )
    },
    [updateItem, trip, tripItem],
  )

  const removeFromItinerary = useCallback(async () => {
    closeSingleDateDropdown()
    closeDateRangeDropdown()
    if (!trip || !tripItem) {
      return
    }
    const payload = {
      ...customItemToUpdatePayload(tripItem),
      isInItinerary: false,
      startDate: undefined,
      endDate: undefined,
    }
    updateItem(
      {
        tripId: trip.id,
        tripItemId: tripItem.id,
        tripItem: payload,
      },
      {
        onSuccess: () => {
          showSnackbar('Removed from itinerary', 'success')
        },
      },
    )
  }, [
    closeDateRangeDropdown,
    closeSingleDateDropdown,
    trip,
    tripItem,
    updateItem,
  ])

  const onSelectSingleDate = useCallback(
    (date: moment.Moment) => {
      closeSingleDateDropdown()
      addItemToItineraryWithDates(date, date)
    },
    [addItemToItineraryWithDates, closeSingleDateDropdown],
  )

  const onSelectDateRange = useCallback(
    (startDate: moment.Moment, endDate?: moment.Moment) => {
      closeDateRangeDropdown()
      addItemToItineraryWithDates(startDate, endDate ?? startDate)
    },
    [addItemToItineraryWithDates, closeDateRangeDropdown],
  )

  const onSelectDateRangeObj = useCallback(
    ({
      startDate,
      endDate,
    }: {
      startDate: moment.Moment
      endDate?: moment.Moment | null
    }) => {
      closeDateRangeDropdown()
      addItemToItineraryWithDates(startDate, endDate ?? startDate)
    },
    [addItemToItineraryWithDates, closeDateRangeDropdown],
  )

  const openDateSelector = useCallback(
    async (tripStartDate: moment.Moment, tripEndDate: moment.Moment) => {
      if (isCustomSingleDateItem(tripItem)) {
        if (isMobile) {
          const selection = await showModal<moment.Moment | null | undefined>(
            <DatesModal
              selectedDate={isInItinerary ? tripItem.startDate : undefined}
              startDate={tripStartDate}
              endDate={tripEndDate}
            />,
          )
          if (selection === null) {
            removeFromItinerary()
          } else if (moment.isMoment(selection)) {
            onSelectSingleDate(selection)
          }
        } else {
          toggleSingleDateDropdown()
        }
      } else {
        if (isMobile) {
          showModal(
            <ModalBase height="full">
              <MobileSelectDatesSelfClose
                initialStartDate={tripItem.startDate}
                initialEndDate={tripItem.endDate}
                onChange={onSelectDateRange}
                onRemove={removeFromItinerary}
              />
            </ModalBase>,
          )
        } else {
          toggleDateRangeDropdown()
        }
      }
    },
    [
      isInItinerary,
      isMobile,
      onSelectDateRange,
      onSelectSingleDate,
      removeFromItinerary,
      showModal,
      toggleDateRangeDropdown,
      toggleSingleDateDropdown,
      tripItem,
    ],
  )

  const onButtonClick = useCallback(async () => {
    if (!trip) {
      return
    } else if (canAddWithOneClick) {
      addToItinerary({ tripId: trip.id, tripItemId: tripItem.id })
    } else if (!trip.startDate || !trip.endDate) {
      onDatesRequiredMessage()
      const didRequestAddTripDates = await showModal<boolean>(
        <AddToItineraryDatesRequiredModal variation="exploreItem" />,
      )

      if (didRequestAddTripDates) {
        onRequestAddTripDates(openDateSelector)
      }
    } else {
      openDateSelector(trip.startDate, trip.endDate)
    }
  }, [
    trip,
    canAddWithOneClick,
    addToItinerary,
    tripItem.id,
    onDatesRequiredMessage,
    showModal,
    onRequestAddTripDates,
    openDateSelector,
  ])

  if (!canEdit || !tripId) {
    return null
  }

  const isAccommodation =
    tripItem.type === 'HOTEL' || tripItem.type === 'AIRBNB'

  return (
    <>
      <BaseBookmarkToItineraryButton
        itineraryDate={isInItinerary ? tripItem.startDate : undefined}
        onClick={onButtonClick}
        isProcessing={isUpdating}
        variant={variant}
        ref={triggerRef}
        withDropdownArrow={!canAddWithOneClick}
      />
      {singleDateDropdownOpen && trip?.startDate && trip.endDate && (
        <DatesDropdown
          isOpen
          onClose={closeSingleDateDropdown}
          onSelectDate={onSelectSingleDate}
          onSelectRemoveDate={removeFromItinerary}
          selectedDate={isInItinerary ? tripItem.startDate : undefined}
          minDate={trip.startDate}
          maxDate={trip.endDate}
          triggerRef={triggerRef}
        />
      )}
      {!isCustomSingleDateItem(tripItem) && dateRangeDropdownOpen && (
        <DateRangeDropdown
          startDate={tripItem.startDate}
          endDate={tripItem.endDate}
          initDate={trip?.startDate}
          triggerRef={triggerRef}
          isOpen
          onDatesChange={onSelectDateRangeObj}
          onClear={removeFromItinerary}
          onClose={closeDateRangeDropdown}
          rangeType={isAccommodation ? 'nights' : 'days'}
        />
      )}
    </>
  )
}

export default ItineraryDateButton
